import React, { useEffect, useState } from 'react'
import moment from 'moment'
import FlightTracking from 'components/FlightTrackingNew/FlightTracking'
import FlightTrackingCard from 'components/FlightTrackingNew/FlightTrackingCard'
import { canonicalFlightKey } from 'components/FlightTrackingNew/flightNervePure'

// Page-lifetime cache: key → { settled, verdict, flights }. A revisit (tab switch, back from
// a sub-page) renders from here instead of paying for another FlightNerve lookup.
const partnerFlightCache = new Map()

const EXPANDED_STYLE = {
  borderTop: '1.5px solid rgba(139, 149, 158, 0.1)',
  borderBottom: '1.5px solid rgba(139, 149, 158, 0.1)',
}

const withTerminal = (info, terminal) =>
  info
    ? { ...info, terminal: info.terminal || (terminal ? 'T' + terminal : null) }
    : info

export const departureLookupDate = list =>
  list.dropOffDate ||
  (list.tripInfo
    ? moment
        .utc(list.pickUpDate)
        .add(list.tripInfo.duration, 'seconds')
        .format()
    : list.pickUpDate)

const CachedFlightCards = ({ flights, terminals, list }) => (
  <div className='flight-card'>
    {flights.map((item, index) => (
      <div
        key={index}
        className='vip-always-expanded-flight'
        style={EXPANDED_STYLE}
      >
        <FlightTrackingCard
          getCity={item.getCity}
          departureTime={item.departureTime}
          arrivalTime={item.arrivalTime}
          delayDepartureTime={item.delayDepartureTime}
          flightInfo={item.flightInfo}
          flightTotalDuration={item.flightTotalDuration}
          progressPercentage={item.progressPercentage}
          delayArrivalTime={item.delayArrivalTime}
          airportPickUpInfo={withTerminal(
            list.airportPickUpInfo,
            terminals?.arrival
          )}
          airportDropOffInfo={withTerminal(
            list.airportDropOffInfo,
            terminals?.departure
          )}
          noData={item.noData}
          time={item.time}
          flightDataLoading={item.flightDataLoading}
          index={index}
          numberOfFlights={flights.length}
        />
      </div>
    ))}
  </div>
)

export default function PartnerFlightInfo({
  direction,
  list,
  mountTracking,
  verified,
  onFetchFlightData,
  fallback,
  // Partner mobile: keep the FlightNerve fetch (date / chauffeur banner) but never
  // show the always-expanded FlightTrackingCard — only the flight-number fallback.
  showExpandedCard = true,
}) {
  const isArrival = direction === 'arrival'
  const info = isArrival ? list.airportPickUpInfo : list.airportDropOffInfo
  const location = isArrival ? list.pickUpLocation : list.dropOffLocation
  const date = isArrival ? list.pickUpDate : departureLookupDate(list)
  const key = [
    direction,
    canonicalFlightKey(info.flightNumber),
    date,
    location?.fullAddress,
  ].join('|')

  const [cached] = useState(() => {
    const entry = partnerFlightCache.get(key)
    return entry &&
      entry.settled &&
      (entry.verdict === null || (entry.flights && entry.flights.length))
      ? entry
      : null
  })

  useEffect(() => {
    if (cached) onFetchFlightData(cached.verdict)
  }, [])

  const record = patch =>
    partnerFlightCache.set(key, {
      ...(partnerFlightCache.get(key) || {}),
      ...patch,
    })

  const handleFetch = data => {
    record({ settled: true, verdict: data })
    onFetchFlightData(data)
  }
  const handleFlights = flights => record({ flights })

  const renderTracking = () => {
    if (cached) {
      return cached.verdict && verified && showExpandedCard ? (
        <div className='driver-flight-tracking-wrap'>
          <CachedFlightCards
            flights={cached.flights}
            terminals={cached.verdict.terminals}
            list={list}
          />
        </div>
      ) : null
    }
    const legProps = isArrival
      ? {
          pickUpDate: date,
          airportPickUpInfo: info,
          callApiPickUp: true,
          cachePickUpInfo: handleFlights,
          pickUpAddress: location?.fullAddress,
          pickUpAirportIata: location?.iata,
        }
      : {
          dropOffDate: date,
          airportDropOffInfo: info,
          callApiDropOff: true,
          callApiPickUp: false,
          cacheDropOffInfo: handleFlights,
          dropOffAddress: location?.fullAddress,
          dropOffAirportIata: location?.iata,
        }
    return (
      <div
        className='driver-flight-tracking-wrap'
        style={{
          display: verified && showExpandedCard ? undefined : 'none',
        }}
      >
        <FlightTracking
          flightNumber={info.flightNumber}
          {...legProps}
          bookingDirection={direction}
          alwaysExpanded
          noMarginTop
          hideResultsCard={!verified || !showExpandedCard}
          onFetchFlightData={handleFetch}
          setBookingLoading={() => {}}
        />
      </div>
    )
  }

  return (
    <>
      {mountTracking && renderTracking()}
      {(!verified || !showExpandedCard) && fallback}
    </>
  )
}
